import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Globe, Mail, Phone, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

const Header = () => {
    const { language, toggleLanguage, t } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const isActive = (path) => location.pathname === path;

    const navLinks = [
        { path: '/products', label: t('collection') },
        { path: '/about-water', label: language === 'no' ? 'Om vann' : 'About Water' },
        { path: '/about', label: t('ourStory') },
    ];

    const themeLinks = [
        { id: 'values-section', label: language === 'no' ? 'Verdiene som veileder oss' : 'The Values That Guide Us', isPage: true, path: '/about', scrollTo: 'values-section' },
        { id: 'gastronomy-section', label: language === 'no' ? 'Restaurant & Gastronomi' : 'Restaurant & Gastronomy', isPage: false },
        { id: 'training-section', label: language === 'no' ? 'Trening & Restitusjon' : 'Training & Recovery', isPage: false },
        { id: 'wellness-section', label: language === 'no' ? 'Helse & Velvære' : 'Health & Wellness', isPage: false },
        { id: 'calculator-section', label: language === 'no' ? 'Mineralkalkulator' : 'Mineral Calculator', isPage: false },
        { id: 'mineral-guide', label: language === 'no' ? 'Mineral Guide' : 'Mineral Guide', isPage: true, path: '/mineral-guide' },
        { id: 'mineral-quiz', label: language === 'no' ? 'Mineral Quiz' : 'Mineral Quiz', isPage: true, path: '/mineral-quiz' },
    ];

    const contactInfo = {
        email: 'hello@rawminerals.no',
        phone: '+47 974 99 879'
    };

    const handleScrollToSection = (sectionId) => {
        if (location.pathname !== '/') {
            // Navigate to home first, then scroll
            navigate('/');
            setTimeout(() => {
                const element = document.querySelector(`[data-testid="${sectionId}"]`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 500);
        } else {
            const element = document.querySelector(`[data-testid="${sectionId}"]`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    const handleLogoClick = (e) => {
        e.preventDefault();
        // Always scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        if (location.pathname !== '/') {
            // Navigate to home page if not already there
            navigate('/');
        }
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-50 header-glass" data-testid="header">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <a 
                        href="/" 
                        onClick={handleLogoClick}
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid="header-logo"
                    >
                        <img 
                            src="https://customer-assets.emergentagent.com/job_raw-water-market/artifacts/tcdfbcdo_raw_minerals_transparent.png" 
                            alt="Raw Minerals" 
                            className="h-20 w-auto"
                        />
                    </a>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-12" data-testid="desktop-nav">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`text-xs uppercase tracking-[0.2em] transition-colors ${
                                    isActive(link.path) 
                                        ? 'text-fjord font-semibold' 
                                        : 'text-stone-600 hover:text-stone-900'
                                }`}
                                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                        
                        {/* Contact Dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger className="text-xs uppercase tracking-[0.2em] text-stone-600 hover:text-stone-900 transition-colors cursor-pointer">
                                {language === 'no' ? 'Kontakt oss' : 'Contact'}
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="center" className="w-56">
                                <DropdownMenuItem asChild>
                                    <a 
                                        href={`mailto:${contactInfo.email}`}
                                        className="flex items-center gap-3 cursor-pointer"
                                    >
                                        <Mail className="w-4 h-4 text-fjord" />
                                        <span>{contactInfo.email}</span>
                                    </a>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <a 
                                        href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                                        className="flex items-center gap-3 cursor-pointer"
                                    >
                                        <Phone className="w-4 h-4 text-fjord" />
                                        <span>{contactInfo.phone}</span>
                                    </a>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </nav>

                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center gap-4">
                        {/* Theme Dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-stone-600 hover:text-stone-900 transition-colors cursor-pointer">
                                {language === 'no' ? 'Utforsk' : 'Explore'}
                                <ChevronDown className="w-3 h-3" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                {themeLinks.map((theme) => (
                                    <DropdownMenuItem 
                                        key={theme.id}
                                        onClick={() => {
                                            if (theme.isPage) {
                                                navigate(theme.path);
                                                if (theme.scrollTo) {
                                                    setTimeout(() => {
                                                        const element = document.querySelector(`[data-testid="${theme.scrollTo}"]`);
                                                        if (element) {
                                                            element.scrollIntoView({ behavior: 'smooth' });
                                                        }
                                                    }, 300);
                                                }
                                            } else {
                                                handleScrollToSection(theme.id);
                                            }
                                        }}
                                        className="cursor-pointer"
                                    >
                                        {theme.label}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Language Toggle */}
                        <button
                            onClick={toggleLanguage}
                            className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-stone-600 hover:text-stone-900 transition-colors"
                            data-testid="language-toggle"
                        >
                            <Globe className="w-4 h-4" />
                            {language === 'no' ? 'EN' : 'NO'}
                        </button>
                    </div>

                    {/* Mobile Menu */}
                    <div className="md:hidden flex items-center gap-4">
                        <button
                            onClick={toggleLanguage}
                            className="text-xs uppercase tracking-wider text-stone-600"
                        >
                            {language === 'no' ? 'EN' : 'NO'}
                        </button>

                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                                    <Menu className="w-5 h-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-[280px]">
                                <nav className="flex flex-col gap-6 mt-12">
                                    {navLinks.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            onClick={() => setMobileOpen(false)}
                                            className="text-lg font-display"
                                            data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                                        >
                                            {link.label}
                                        </Link>
                                    ))}
                                    
                                    {/* Contact Section */}
                                    <div className="pt-2">
                                        <p className="text-xs uppercase tracking-wider text-stone-400 mb-3">
                                            {language === 'no' ? 'Kontakt oss' : 'Contact'}
                                        </p>
                                        <a 
                                            href={`mailto:${contactInfo.email}`}
                                            className="flex items-center gap-3 text-stone-700 mb-3"
                                        >
                                            <Mail className="w-4 h-4 text-fjord" />
                                            <span className="text-sm">{contactInfo.email}</span>
                                        </a>
                                        <a 
                                            href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                                            className="flex items-center gap-3 text-stone-700"
                                        >
                                            <Phone className="w-4 h-4 text-fjord" />
                                            <span className="text-sm">{contactInfo.phone}</span>
                                        </a>
                                    </div>
                                    
                                    <div className="h-px bg-stone-200 my-4" />
                                    
                                    {/* Theme Links */}
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-stone-400 mb-3">
                                            {language === 'no' ? 'Utforsk' : 'Explore'}
                                        </p>
                                        {themeLinks.map((theme) => (
                                            <button
                                                key={theme.id}
                                                onClick={() => {
                                                    setMobileOpen(false);
                                                    
                                                    setTimeout(() => {
                                                        if (theme.isPage) {
                                                            navigate(theme.path);
                                                            if (theme.scrollTo) {
                                                                setTimeout(() => {
                                                                    const element = document.querySelector(`[data-testid="${theme.scrollTo}"]`);
                                                                    if (element) {
                                                                        element.scrollIntoView({ behavior: 'smooth' });
                                                                    }
                                                                }, 500);
                                                            }
                                                        } else {
                                                            // For section links (not pages)
                                                            if (location.pathname !== '/') {
                                                                navigate('/');
                                                                setTimeout(() => {
                                                                    const element = document.querySelector(`[data-testid="${theme.id}"]`);
                                                                    if (element) {
                                                                        element.scrollIntoView({ behavior: 'smooth' });
                                                                    }
                                                                }, 600);
                                                            } else {
                                                                const element = document.querySelector(`[data-testid="${theme.id}"]`);
                                                                if (element) {
                                                                    element.scrollIntoView({ behavior: 'smooth' });
                                                                }
                                                            }
                                                        }
                                                    }, 300);
                                                }}
                                                className="block text-stone-700 mb-3 text-sm text-left"
                                            >
                                                {theme.label}
                                            </button>
                                        ))}
                                    </div>
                                </nav>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
